import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Loader2, Receipt } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import {
  IssuerViaLabel,
  type CaseListResponse,
  type ClientListResponse,
  type InvoiceDetail,
  type InvoiceSeriesDto,
  type ResolvedBilling,
} from '@toga/shared'

export default function NewInvoicePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [series, setSeries] = useState<InvoiceSeriesDto[]>([])
  const [seriesId, setSeriesId] = useState('')
  const [clients, setClients] = useState<ClientListResponse['items']>([])
  const [clientId, setClientId] = useState(searchParams.get('clientId') ?? '')
  const [cases, setCases] = useState<CaseListResponse['items']>([])
  const [caseId, setCaseId] = useState(searchParams.get('caseId') ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // A4: la serie que corresponde según expediente, abogado responsable, cliente o bufete.
  const [suggested, setSuggested] = useState<ResolvedBilling | null>(null)
  const [suggestionProblem, setSuggestionProblem] = useState<string | null>(null)
  const [seriesTouched, setSeriesTouched] = useState(false)

  useEffect(() => {
    api
      .get<{ items: InvoiceSeriesDto[] }>('/invoice-series')
      .then((r) => {
        // Las rectificativas nacen de «Rectificar» en la factura original, no de aquí.
        const usable = r.items.filter((s) => s.kind !== 'RECTIFICATIVA')
        setSeries(usable)
        const def = usable.find((s) => s.isDefault) ?? usable[0]
        if (def) setSeriesId(def.id)
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : 'No se pudieron cargar las series')
      )
  }, [])

  const loadClients = useCallback(async (q: string) => {
    try {
      const params = new URLSearchParams({ pageSize: '30' })
      if (q.trim()) params.set('q', q.trim())
      const resp = await api.get<ClientListResponse>(`/clients?${params.toString()}`)
      setClients(resp.items)
    } catch {
      setClients([])
    }
  }, [])

  useEffect(() => {
    void loadClients('')
  }, [loadClients])

  useEffect(() => {
    if (!clientId) {
      setCases([])
      return
    }
    api
      .get<CaseListResponse>(`/cases?clientId=${clientId}&pageSize=30`)
      .then((r) => setCases(r.items))
      .catch(() => setCases([]))
  }, [clientId])

  useEffect(() => {
    setSuggested(null)
    setSuggestionProblem(null)
    if (!clientId) return
    let alive = true
    const qs = new URLSearchParams({ clientId })
    if (caseId) qs.set('caseId', caseId)
    api
      .get<ResolvedBilling>(`/billing/resolve-issuer?${qs}`)
      .then((r) => {
        if (!alive) return
        setSuggested(r)
        if (!seriesTouched) setSeriesId(r.series.id)
      })
      .catch((err) => {
        if (!alive) return
        const body =
          err instanceof ApiError ? (err.body as { error?: string } | undefined) : undefined
        setSuggestionProblem(body?.error ?? null)
      })
    return () => {
      alive = false
    }
    // seriesTouched no debe relanzar la consulta: solo cuenta el valor al responder.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, caseId])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!seriesId || !clientId) {
      setError('Indica serie y cliente')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const { invoice } = await api.post<{ invoice: InvoiceDetail }>('/invoices', {
        seriesId,
        clientId,
        caseId: caseId || null,
        lines: [],
      })
      navigate(`/billing/${invoice.id}`, { replace: true })
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4 animate-slideUp">
      <header>
        <Link
          to="/billing"
          className="text-xs t-faint hover:underline inline-flex items-center gap-1"
        >
          <ArrowLeft size={12} /> Facturación
        </Link>
        <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2 mt-1">
          <Receipt size={18} className="text-toga-300" />
          Nueva factura
        </h1>
      </header>

      <form onSubmit={onSubmit} className="surface p-5 max-w-xl space-y-4">
        <label className="block">
          <span className="text-xs t-muted">Serie</span>
          <select
            value={seriesId}
            onChange={(e) => {
              setSeriesId(e.target.value)
              setSeriesTouched(true)
            }}
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
            required
          >
            {series.length === 0 && <option value="">(crea una serie primero)</option>}
            {series.map((s) => (
              <option key={s.id} value={s.id}>
                {s.prefix} · {s.name} — {s.issuerName}
              </option>
            ))}
          </select>
        </label>

        {(suggested || suggestionProblem) && (
          <p className={`text-xs -mt-2 ${suggestionProblem ? 'text-amber-300' : 't-faint'}`}>
            {suggestionProblem ??
              `Corresponde facturar con ${suggested!.issuer.name} (${suggested!.issuer.nif}), ${IssuerViaLabel[suggested!.via]}.`}
          </p>
        )}

        <label className="block">
          <span className="text-xs t-muted">Cliente</span>
          <input
            placeholder="Buscar cliente…"
            onChange={(e) => void loadClients(e.target.value)}
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5 mb-2"
          />
          <select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary"
            required
            size={6}
          >
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName}
              </option>
            ))}
          </select>
        </label>

        {cases.length > 0 && (
          <label className="block">
            <span className="text-xs t-muted">Expediente (opcional)</span>
            <select
              value={caseId}
              onChange={(e) => setCaseId(e.target.value)}
              className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
            >
              <option value="">— sin expediente —</option>
              {cases.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} · {c.title}
                </option>
              ))}
            </select>
          </label>
        )}

        {error && <p className="text-xs text-rose-300">{error}</p>}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={busy}
            className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Crear borrador
          </button>
        </div>
      </form>
    </div>
  )
}
